import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import GlobeCanvas from "./GlobeCanvas";
import { CountUp } from "./Reveal";

/* Hero-right: the master TNX coin over globe, orbital rings + brand HUDs (no pricing — TNX is pre-listing) */
export default function CoinHero() {
    const ref = useRef(null);
    const rxRaw = useMotionValue(0);
    const ryRaw = useMotionValue(0);
    const rx = useSpring(rxRaw, { stiffness: 50, damping: 14 });
    const ry = useSpring(ryRaw, { stiffness: 50, damping: 14 });

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        ryRaw.set(((e.clientX - r.left) / r.width - 0.5) * 10);
        rxRaw.set(((e.clientY - r.top) / r.height - 0.5) * -8);
    };
    const onLeave = () => {
        rxRaw.set(0);
        ryRaw.set(0);
    };

    return (
        <div className="relative lg:col-span-6">
            <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
            >
                <div
                    ref={ref}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    data-testid="hero-coin-scene"
                    className="relative mx-auto flex h-[440px] w-full max-w-[560px] items-center justify-center [perspective:1400px] sm:h-[520px] lg:h-[620px]"
                >
                    {/* backdrop: subtle globe + ambient glow (code-built) */}
                    <div className="absolute inset-[12%] opacity-30">
                        <GlobeCanvas />
                    </div>
                    <div className="absolute inset-[18%] rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.15),rgba(139,92,246,0.07)_55%,transparent_75%)]" />

                    <motion.div
                        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                        className="relative z-10 h-full w-full"
                    >
                        {/* tilted orbital rings */}
                        <div
                            className="absolute inset-[2%]"
                            style={{ transform: "rotateX(76deg)", transformStyle: "preserve-3d" }}
                        >
                            <div className="relative h-full w-full animate-spin-slow rounded-full border border-cyan-400/25">
                                <span className="absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyber shadow-[0_0_12px_rgba(0,209,255,0.9)]" />
                            </div>
                        </div>
                        <div
                            className="absolute inset-[13%]"
                            style={{ transform: "rotateX(76deg) rotateY(-16deg)", transformStyle: "preserve-3d" }}
                        >
                            <div className="h-full w-full rounded-full border border-roy/25" />
                        </div>
                        <div className="absolute inset-[24%] rounded-full border border-white/[0.05]" />

                        {/* coin glow */}
                        <div className="absolute left-1/2 top-1/2 h-[68%] w-[68%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.22),rgba(139,92,246,0.1)_50%,transparent_72%)] blur-2xl" />

                        {/* the TNX coin */}
                        <div className="absolute inset-0 z-10 m-auto w-[76%] max-w-[470px] animate-float-y sm:w-[72%]">
                            <img
                                src={`${process.env.PUBLIC_URL}/brand/tvx-coin-hero.png`}
                                alt="TNX — TAVONX Utility Token"
                                draggable={false}
                                data-testid="hero-coin-visual"
                                className="w-full select-none drop-shadow-[0_0_70px_rgba(0,209,255,0.35)]"
                            />
                        </div>

                        {/* floating brand HUDs — no pricing, TNX is pre-listing */}
                        <motion.div
                            data-testid="hero-ticker-tvx"
                            className="absolute left-0 top-[13%] z-20 sm:left-[2%]"
                            style={{ transform: "translateZ(70px)" }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.9, delay: 1 }}
                        >
                            <div
                                className="glass flex animate-float-y items-center gap-3 rounded-lg px-3.5 py-2.5"
                                style={{ animationDelay: "0.8s" }}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/brand/tvx-icon.png`}
                                    alt="TNX"
                                    draggable={false}
                                    className="h-8 w-8 select-none"
                                />
                                <div className="leading-tight">
                                    <div className="font-mono text-[10px] text-slate-300">TNX · Utility Token</div>
                                    <div className="font-mono text-xs font-medium text-white">
                                        Genesis Launch · 28 Oct 2026
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            data-testid="hero-hud-supply"
                            className="absolute right-0 top-[46%] z-20 sm:right-[1%]"
                            style={{ transform: "translateZ(90px)" }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.9, delay: 1.15 }}
                        >
                            <div
                                className="glass animate-float-y rounded-lg px-3.5 py-2.5"
                                style={{ animationDelay: "1.6s" }}
                            >
                                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">
                                    Max Supply
                                </div>
                                <div className="mt-1 font-mono text-sm font-medium text-white">
                                    <CountUp to={1000000} decimals={0} /> <span className="text-[10px] tracking-[0.14em] text-cyber">TNX</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            data-testid="hero-hud-market"
                            className="absolute bottom-[5%] left-[8%] z-20"
                            style={{ transform: "translateZ(55px)" }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.9, delay: 1.3 }}
                        >
                            <div
                                className="glass flex animate-float-y items-center gap-2.5 rounded-lg px-3.5 py-2.5"
                                style={{ animationDelay: "2.4s" }}
                            >
                                <span className="h-1.5 w-1.5 animate-blink rounded-full bg-cyber" />
                                <div className="leading-tight">
                                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">
                                        Ecosystem
                                    </div>
                                    <div className="font-mono text-[11px] text-white">
                                        Built for velocity · Powered by liquidity
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.26em] text-slate-600">
                    TNX · Utility Token — Pre-launch
                </p>
            </motion.div>
        </div>
    );
}
